import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/contexts/ToastContext";
import {
  catalogApi,
  CreateCatalogRequest,
  ItemCopyDetails,
  CatalogMatchResult,
} from "@/lib/api/catalog";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { useItemTypes } from "@/hooks/useItemTypes";
import { useLocations } from "@/hooks/useLocations";
import { useCollections } from "@/hooks/useCollections";
import { useBookConditions } from "@/hooks/useBookConditions";
import { useItemStatuses } from "@/hooks/useItemStatuses";
import { useCatalogSettings } from "@/hooks/useCatalogSettings";
import { CopyDetailsEditor, ItemCopyData } from "@/components/catalog/CopyDetailsEditor";
import { ExistingCopyEditor } from "@/components/catalog/ExistingCopyEditor";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { useAccessControl } from "@/hooks/useAccessControl";
import { isFieldRequiredOnPublish } from "@/lib/catalog/catalog-required-fields";
import { validateCatalogForm } from "@/lib/catalog/catalog-validate-form";
import { CatalogPublicationBanner } from "@/components/catalog/CatalogPublicationBanner";
import { CatalogWorkflowButtons } from "@/components/catalog/CatalogWorkflowButtons";
import { AddCopyFormSection } from "@/components/catalog/AddCopyFormSection";
import { CatalogMobileActionBar } from "@/components/catalog/CatalogMobileActionBar";
import {
  executeCatalogWorkflowAction,
  isCatalogRecordReadOnly,
  type CatalogWorkflowAction,
  type PublicationStatus,
} from "@/lib/catalog/catalog-workflow";
import { resolveCatalogEntryIntent } from "@/lib/catalog/catalog-entry-intent";
import { isValidIsbn } from "@/lib/isbn";
import { formatNumberWithCommas } from "@/lib/formatters";
import {
  BookOpen,
  Plus,
  Minus,
  X,
  AlertCircle,
  User,
  Building,
  Calendar,
  Hash,
  FileText,
  Package,
  MapPin,
  Layers,
  Tag,
  Barcode,
  BookMarked,
  Info,
  ClipboardCheck,
  CheckCircle,
} from "lucide-react";
import { CatalogCollapsibleFormSection } from "@/components/catalog/CatalogCollapsibleFormSection";
import { cn } from "@/lib/utils";

type ManualEntryStep = "identifier" | "form";

const ADD_COPY_ERROR_FIELDS = new Set([
  "itemTypeId",
  "locationId",
  "branchId",
  "conditionCode",
  "statusCode",
  "acquisitionMode",
  "accession",
]);

export function ManualCatalogPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { hasPermission } = useAccessControl();
  const canEditCopy = hasPermission("catalog.edit") || hasPermission("book.edit");
  const canDeleteCopy = hasPermission("catalog.delete") || hasPermission("book.delete");

  const isEditMode = !!id;
  const [entryStep, setEntryStep] = useState<ManualEntryStep>(isEditMode ? "form" : "identifier");
  const [matchResult, setMatchResult] = useState<CatalogMatchResult | null>(null);
  const [checkingMatch, setCheckingMatch] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingBook, setLoadingBook] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [publicationStatus, setPublicationStatus] = useState<PublicationStatus | undefined>();
  const [reviewNotes, setReviewNotes] = useState<string | null>(null);

  // Reference data (cached via React Query)
  const { data: itemTypesRaw = [], isLoading: loadingTypes } = useItemTypes(true);
  const { data: locationsRaw = [], isLoading: loadingLocs } = useLocations(true);
  const { data: collectionsRaw = [], isLoading: loadingCols } = useCollections(true);
  const { data: conditionsRaw = [], isLoading: loadingConds } = useBookConditions(true);
  const { data: itemStatuses = [], isLoading: loadingStatuses } = useItemStatuses();
  const { data: catalogSettingsData, isLoading: loadingCatalogSettings } = useCatalogSettings();
  const { data: branches = [] } = useActiveBranches();

  const itemTypes = itemTypesRaw.filter((t) => t.isActive);
  const locations = locationsRaw.filter((l) => l.isActive);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const conditions = conditionsRaw.filter((c) => c.isActive).sort((a, b) => a.order - b.order);
  const catalogSettings = catalogSettingsData ?? {
    accessionNumberMode: "AUTO" as const,
    approvalWorkflow: "disabled" as const,
  };
  const isReadOnly = isCatalogRecordReadOnly(publicationStatus);

  const loadingOptions =
    loadingTypes ||
    loadingLocs ||
    loadingCols ||
    loadingConds ||
    loadingStatuses ||
    loadingCatalogSettings;

  // Existing items (edit mode) - for viewing and editing current copies
  const [existingItems, setExistingItems] = useState<any[]>([]);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);

  // Add Copies (edit mode)
  const [showAddCopies, setShowAddCopies] = useState(false);
  const [addingCopies, setAddingCopies] = useState(false);
  const [newCopiesCount, setNewCopiesCount] = useState(1);
  const [newCopyItemDefaults, setNewCopyItemDefaults] = useState<
    CreateCatalogRequest["itemDefaults"]
  >({
    itemTypeId: "",
    locationId: "",
    collectionId: "",
    branchId: "",
    conditionCode: "",
    statusCode: "AVAILABLE",
    acquisitionMode: "",
  });
  const [newCopyData, setNewCopyData] = useState<ItemCopyData[]>([{}]);

  // Form data
  const [formData, setFormData] = useState<CreateCatalogRequest>({
    biblio: {
      title: "",
      subtitle: "",
      authors: [],
      editors: [],
      edition: "",
      isbn: "",
      doi: "",
      publisher: "",
      placeOfPublication: "",
      publishedDate: "",
      pages: undefined,
      illustrations: "",
      size: "",
      seriesInformation: "",
      subjects: [],
      description: "",
      coverUrl: "",
      lcClassification: "",
      callNumber: "",
      bindingType: "",
    },
    copies: 1,
    itemDefaults: {
      itemTypeId: "",
      locationId: "",
      collectionId: "",
      branchId: "",
      conditionCode: "",
      statusCode: "AVAILABLE",
      acquisitionMode: "",
    },
  });

  const [authorInput, setAuthorInput] = useState("");
  const [editorInput, setEditorInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");

  const [copyData, setCopyData] = useState<ItemCopyData[]>([{}]);
  const catalogEntryIntent = resolveCatalogEntryIntent(matchResult);
  const isIdentifierStep = !isEditMode && entryStep === "identifier";
  const isAddCopyCreateFlow =
    !isEditMode && entryStep === "form" && catalogEntryIntent === "add_copy";
  const shouldShowCreateForm = !isEditMode && entryStep === "form" && catalogEntryIntent === "new";
  const cleanedIdentifierIsbn = (formData.biblio.isbn ?? "").replace(/[-\s]/g, "").trim();
  const isIdentifierIsbnValid =
    cleanedIdentifierIsbn.length > 0 && isValidIsbn(cleanedIdentifierIsbn);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Load book data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadBookData(id);
    }
  }, [id, isEditMode]);

  const loadBookData = async (bookId: string) => {
    try {
      setLoadingBook(true);
      const bookData = await catalogApi.getBiblio(bookId);

      // Store existing items for editing
      setExistingItems(bookData.Items || []);

      // Pre-fill form with existing book data
      setFormData({
        biblio: {
          title: bookData.title || "",
          subtitle: bookData.subtitle || "",
          authors: bookData.authors || [],
          editors: bookData.editors || [],
          edition: bookData.edition || "",
          isbn: bookData.isbn || "",
          doi: bookData.doi || "",
          publisher: bookData.publisher || "",
          placeOfPublication: bookData.placeOfPublication || "",
          publishedDate: bookData.publishedDate || "",
          pages: bookData.pages || undefined,
          illustrations: bookData.illustrations || "",
          size: bookData.size || "",
          seriesInformation: bookData.seriesInformation || "",
          subjects: bookData.subjects || [],
          description: bookData.description || "",
          coverUrl: bookData.coverUrl || "",
          lcClassification: bookData.lcClassification || "",
          callNumber: bookData.callNumber || "",
          bindingType: bookData.bindingType || "",
        },
        copies: bookData.Items?.length || 1,
        itemDefaults: {
          itemTypeId: bookData.Items?.[0]?.itemTypeId || "",
          locationId: bookData.Items?.[0]?.locationId || "",
          collectionId: bookData.Items?.[0]?.collectionId || "",
          branchId: bookData.Items?.[0]?.branchId || "",
          conditionCode: bookData.Items?.[0]?.conditionCode || "",
          statusCode: bookData.Items?.[0]?.statusCode || "AVAILABLE",
          acquisitionMode: bookData.Items?.[0]?.acquisitionMode || "",
        },
      });

      setPublicationStatus(bookData.publicationStatus || "PUBLISHED");
      setReviewNotes(bookData.reviewNotes || null);

      // Don't mark as unsaved when loading existing data
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error("Failed to load book:", error);
      toast.error(error.response?.data?.message || "Failed to load book data.");
      navigate("/catalog");
    } finally {
      setLoadingBook(false);
    }
  };

  useEffect(() => {
    if (isEditMode) return;

    const hasBiblioChanges = Boolean(
      formData.biblio.title ||
        formData.biblio.subtitle ||
        formData.biblio.isbn ||
        formData.biblio.doi ||
        formData.biblio.publisher ||
        formData.biblio.placeOfPublication ||
        formData.biblio.publishedDate ||
        formData.biblio.pages ||
        formData.biblio.illustrations ||
        formData.biblio.size ||
        formData.biblio.seriesInformation ||
        formData.biblio.description ||
        formData.biblio.coverUrl ||
        formData.biblio.lcClassification ||
        formData.biblio.callNumber ||
        formData.biblio.bindingType ||
        formData.biblio.authors?.length ||
        formData.biblio.editors?.length ||
        formData.biblio.subjects?.length
    );
    const hasItemDefaults = Object.values(formData.itemDefaults).some(
      (value) => value !== undefined && value !== ""
    );
    const hasCopyDetails = copyData.some((copy) =>
      Object.values(copy).some((value) => value !== undefined && value !== "")
    );

    if (
      hasBiblioChanges ||
      formData.copies !== 1 ||
      hasItemDefaults ||
      hasCopyDetails ||
      entryStep === "form"
    ) {
      setHasUnsavedChanges(true);
    }
  }, [copyData, entryStep, formData, isEditMode]);

  const addAuthor = () => {
    if (authorInput.trim() && !formData.biblio.authors?.includes(authorInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        biblio: {
          ...prev.biblio,
          authors: [...(prev.biblio.authors || []), authorInput.trim()],
        },
      }));
      setAuthorInput("");
    }
  };

  const removeAuthor = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      biblio: {
        ...prev.biblio,
        authors: prev.biblio.authors?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  const addEditor = () => {
    if (editorInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        biblio: {
          ...prev.biblio,
          editors: [...(prev.biblio.editors || []), editorInput.trim()],
        },
      }));
      setEditorInput("");
    }
  };

  const removeEditor = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      biblio: {
        ...prev.biblio,
        editors: (prev.biblio.editors || []).filter((_, i) => i !== index),
      },
    }));
  };

  const addSubject = () => {
    if (subjectInput.trim() && !formData.biblio.subjects?.includes(subjectInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        biblio: {
          ...prev.biblio,
          subjects: [...(prev.biblio.subjects || []), subjectInput.trim()],
        },
      }));
      setSubjectInput("");
    }
  };

  const removeSubject = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      biblio: {
        ...prev.biblio,
        subjects: prev.biblio.subjects?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  const buildCreatePayload = (): CreateCatalogRequest => {
    const cleanedItemDefaults = {
      itemTypeId: formData.itemDefaults.itemTypeId || undefined,
      locationId: formData.itemDefaults.locationId || undefined,
      collectionId: formData.itemDefaults.collectionId || undefined,
      branchId: formData.itemDefaults.branchId || undefined,
      conditionCode: formData.itemDefaults.conditionCode || undefined,
      statusCode: formData.itemDefaults.statusCode || "AVAILABLE",
    };

    const itemCopies: ItemCopyDetails[] = copyData.map((copy) => ({
      accessionNumber: copy.accessionNumber,
      conditionCode: copy.conditionCode,
      statusCode: copy.statusCode,
      acquisitionMode: copy.acquisitionMode,
      locationId: copy.locationId,
      branchId: copy.branchId,
    }));

    return {
      ...formData,
      itemDefaults: cleanedItemDefaults,
      itemCopies,
      catalogMethod: "MANUAL",
    };
  };

  const buildAddCopyPayload = () => ({
    copies: formData.copies,
    itemDefaults: {
      itemTypeId: formData.itemDefaults.itemTypeId || undefined,
      locationId: formData.itemDefaults.locationId || undefined,
      collectionId: formData.itemDefaults.collectionId || undefined,
      branchId: formData.itemDefaults.branchId || undefined,
      conditionCode: formData.itemDefaults.conditionCode || undefined,
      statusCode: formData.itemDefaults.statusCode || "AVAILABLE",
      acquisitionMode: formData.itemDefaults.acquisitionMode || undefined,
    },
    itemCopies: copyData.map((copy) => ({
      accessionNumber: copy.accessionNumber,
      conditionCode: copy.conditionCode,
      statusCode: copy.statusCode,
      acquisitionMode: copy.acquisitionMode,
      locationId: copy.locationId,
      branchId: copy.branchId,
    })),
  });
  const validateAddCopyForm = (): boolean => {
    const itemErrors = validateCatalogForm(
      {
        biblio: {
          title: matchResult?.biblio?.title || "Existing catalog record",
          authors: matchResult?.biblio?.authors || ["Existing catalog record"],
          isbn: matchResult?.biblio?.isbn || formData.biblio.isbn,
          subjects: ["Existing catalog record"],
        },
        copies: formData.copies,
        itemDefaults: formData.itemDefaults,
        itemCopies: copyData,
      },
      {
        method: "MANUAL",
        mode: "publish",
        accessionNumberMode: catalogSettings.accessionNumberMode,
        requireBranch: branches.length > 0,
      }
    );
    const filteredErrors = Object.fromEntries(
      Object.entries(itemErrors).filter(([key]) => ADD_COPY_ERROR_FIELDS.has(key))
    );

    setErrors(filteredErrors);
    setTouched(new Set(Object.keys(filteredErrors)));
    return Object.keys(filteredErrors).length === 0;
  };

  const handleIdentifierContinue = async () => {
    if (!isIdentifierIsbnValid) return;

    setCheckingMatch(true);
    setErrors({});
    setTouched(new Set());

    try {
      const match = await catalogApi.matchCatalog({ isbn: cleanedIdentifierIsbn });
      setMatchResult(match);

      if (resolveCatalogEntryIntent(match) === "add_copy") {
        setFormData((prev) => ({
          ...prev,
          biblio: {
            ...prev.biblio,
            isbn: cleanedIdentifierIsbn,
          },
          copies: 1,
          itemDefaults: {
            ...prev.itemDefaults,
            itemTypeId: match.suggestedItemDefaults?.itemTypeId || prev.itemDefaults.itemTypeId,
            locationId: match.suggestedItemDefaults?.locationId || prev.itemDefaults.locationId,
            collectionId:
              match.suggestedItemDefaults?.collectionId || prev.itemDefaults.collectionId,
            branchId: match.suggestedItemDefaults?.branchId || prev.itemDefaults.branchId,
            conditionCode:
              match.suggestedItemDefaults?.conditionCode || prev.itemDefaults.conditionCode,
            statusCode:
              match.suggestedItemDefaults?.statusCode ||
              prev.itemDefaults.statusCode ||
              "AVAILABLE",
            acquisitionMode:
              match.suggestedItemDefaults?.acquisitionMode || prev.itemDefaults.acquisitionMode,
          },
        }));
        setCopyData([
          {
            conditionCode: match.suggestedItemDefaults?.conditionCode,
            statusCode: match.suggestedItemDefaults?.statusCode || "AVAILABLE",
            acquisitionMode: match.suggestedItemDefaults?.acquisitionMode,
            locationId: match.suggestedItemDefaults?.locationId,
            branchId: match.suggestedItemDefaults?.branchId,
          },
        ]);
      } else {
        setFormData((prev) => ({
          ...prev,
          biblio: {
            ...prev.biblio,
            isbn: cleanedIdentifierIsbn,
          },
        }));
      }

      setEntryStep("form");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to check for existing catalog records.");
    } finally {
      setCheckingMatch(false);
    }
  };

  const handleAddCopySubmit = async () => {
    if (!matchResult?.biblioId) return;
    if (!validateAddCopyForm()) {
      toast.error("Please complete the required copy details before adding copies.");
      return;
    }

    setLoading(true);
    try {
      const result = await catalogApi.addCopiesToBiblio(
        matchResult.biblioId,
        buildAddCopyPayload()
      );
      toast.success(
        `${result.items.length} ${result.items.length === 1 ? "copy" : "copies"} added successfully!`
      );
      setHasUnsavedChanges(false);
      navigate(`/catalog/physical/${matchResult.biblioId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add copies.");
    } finally {
      setLoading(false);
    }
  };

  const validateForAction = (action: CatalogWorkflowAction): boolean => {
    const newErrors = validateCatalogForm(
      {
        biblio: formData.biblio,
        copies: formData.copies,
        itemDefaults: formData.itemDefaults,
        itemCopies: copyData,
      },
      {
        method: "MANUAL",
        mode: action === "draft" ? "draft" : "publish",
        accessionNumberMode: catalogSettings.accessionNumberMode,
        skipItemValidation: isEditMode,
        requireBranch: branches.length > 0,
      }
    );

    setErrors(newErrors);
    setTouched(new Set(Object.keys(newErrors)));
    return Object.keys(newErrors).length === 0;
  };

  const handleWorkflowAction = async (action: CatalogWorkflowAction) => {
    if (isReadOnly) return;
    if (!validateForAction(action)) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && id) {
        const requiresSubmitPayload =
          (action === "submit-for-review" || action === "publish") &&
          publicationStatus !== "PUBLISHED";
        await executeCatalogWorkflowAction({
          action,
          biblioId: id,
          biblioUpdate: formData.biblio,
          createPayload: requiresSubmitPayload ? buildCreatePayload() : undefined,
          publicationStatus,
        });
        toast.success(
          action === "submit-for-review"
            ? `"${formData.biblio.title}" submitted for review.`
            : `"${formData.biblio.title}" updated successfully!`
        );
      } else {
        const result = await executeCatalogWorkflowAction({
          action,
          createPayload: buildCreatePayload(),
        });
        toast.success(
          action === "draft"
            ? `"${formData.biblio.title}" saved as draft.`
            : action === "submit-for-review"
              ? `"${formData.biblio.title}" submitted for review.`
              : `"${formData.biblio.title}" cataloged successfully!`
        );
        if (action === "draft") {
          navigate(`/catalog/physical/${result.biblioId}/edit`);
          return;
        }
      }

      setHasUnsavedChanges(false);
      navigate(action === "draft" ? "/staff/catalog/drafts" : "/catalog");
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || `Failed to ${isEditMode ? "update" : "catalog"} book.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAddCopyCreateFlow) {
      handleAddCopySubmit();
      return;
    }
    handleWorkflowAction(isEditMode ? "save-changes" : "publish");
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      navigate("/catalog");
    }
  };

  const handleDiscard = () => {
    setHasUnsavedChanges(false);
    navigate("/catalog");
  };

  const incrementCopies = () =>
    setFormData((prev) => ({ ...prev, copies: Math.min(prev.copies + 1, 100) }));
  const decrementCopies = () =>
    setFormData((prev) => ({ ...prev, copies: Math.max(prev.copies - 1, 1) }));

  if (loadingOptions || loadingBook) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title={isEditMode ? "Edit Book" : "Manual Catalog Entry"}
            description={
              isEditMode ? "Update book details" : "Catalog a book by entering all details manually"
            }
            breadcrumbItems={[
              { label: "Catalog", href: "/catalog" },
              { label: isEditMode ? "Edit Book" : "Manual Entry" },
            ]}
            showSearch={false}
          />
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner
              size="xl"
              label={loadingBook ? "Loading book details..." : "Loading cataloging tools..."}
            />
          </div>
        </div>
      </div>
    );
  }

  const addCopyActionButton = (
    <Button
      type="button"
      onClick={handleAddCopySubmit}
      disabled={loading}
      className="w-full lg:w-auto"
    >
      {loading ? (
        <>
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Adding...
        </>
      ) : (
        "Add copy"
      )}
    </Button>
  );

  if (isIdentifierStep) {
    const continueButton = (
      <Button
        type="button"
        onClick={handleIdentifierContinue}
        disabled={!isIdentifierIsbnValid || checkingMatch}
        className="min-h-11 w-full lg:w-auto"
      >
        {checkingMatch ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Checking...
          </>
        ) : (
          "Continue"
        )}
      </Button>
    );

    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="Manual Catalog Entry"
            description="Start with the ISBN so we can check whether this book is already in your library."
            breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: "Manual Entry" }]}
            showSearch={false}
          />

          <div className="mx-auto max-w-7xl px-4 pb-24 lg:px-0 lg:pb-0">
            <Card className="overflow-hidden bg-white shadow-sm">
              <div className="space-y-6 p-6 sm:p-8">
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold text-gray-900">Enter ISBN first</h2>
                  <p className="text-sm text-gray-600">
                    Enter ISBN first to check if this book already exists in your library.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="manual-catalog-isbn"
                    className="text-sm font-medium text-gray-700"
                  >
                    ISBN
                  </Label>
                  <Input
                    id="manual-catalog-isbn"
                    value={formData.biblio.isbn}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        biblio: { ...prev.biblio, isbn: e.target.value },
                      }))
                    }
                    placeholder="9–13 digits (dashes optional)"
                    className="h-14 text-base"
                  />
                  <p className="text-xs text-gray-500">
                    Enter an ISBN with 9–13 digits. Dashes are optional.
                  </p>
                </div>

                <div className="hidden justify-end lg:flex">{continueButton}</div>
              </div>
            </Card>
          </div>
        </div>

        <CatalogMobileActionBar>{continueButton}</CatalogMobileActionBar>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title={isEditMode ? "Edit Book" : "Manual Catalog Entry"}
          description={
            isEditMode
              ? "Update bibliographic details for this book"
              : "Catalog a book by entering all the bibliographic and item details manually"
          }
          breadcrumbItems={[
            { label: "Catalog", href: "/catalog" },
            { label: isEditMode ? "Edit Book" : "Manual Entry" },
          ]}
          showSearch={false}
        />

        {publicationStatus ? (
          <CatalogPublicationBanner status={publicationStatus} reviewNotes={reviewNotes} />
        ) : null}

        <form onSubmit={handleSubmit}>
          <fieldset disabled={isReadOnly} className="disabled:opacity-80">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Main Form */}
              <div
                className={cn(
                  "space-y-6 lg:col-span-2",
                  !isEditMode && entryStep === "form" && "pb-24 lg:pb-0"
                )}
              >
                {isAddCopyCreateFlow ? (
                  <>
                    <AddCopyFormSection
                      copies={formData.copies}
                      onCopiesChange={(copies) =>
                        setFormData((prev) => ({
                          ...prev,
                          copies,
                        }))
                      }
                      copyData={copyData}
                      onCopyDataChange={setCopyData}
                      itemDefaults={formData.itemDefaults}
                      onItemDefaultsChange={(itemDefaults) =>
                        setFormData((prev) => ({
                          ...prev,
                          itemDefaults,
                        }))
                      }
                      conditions={conditions}
                      itemStatuses={itemStatuses}
                      locations={locations}
                      branches={branches}
                      itemTypes={itemTypes}
                      collections={collections}
                      catalogSettings={catalogSettings}
                      onSubmit={handleAddCopySubmit}
                      loading={loading}
                      submitLabel="Add copy"
                      showDesktopSubmit={false}
                      mobileActions={
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            className="w-full"
                          >
                            Cancel
                          </Button>
                          {addCopyActionButton}
                        </>
                      }
                      matchSummary={
                        matchResult?.biblio && matchResult.biblioId
                          ? {
                              biblio: matchResult.biblio,
                              existingCopyCount: matchResult.existingCopyCount,
                              biblioId: matchResult.biblioId,
                              matchLabel: matchResult.matchType === "issn" ? "ISSN" : "ISBN",
                            }
                          : undefined
                      }
                    />

                    {(errors.itemTypeId ||
                      errors.locationId ||
                      errors.conditionCode ||
                      errors.statusCode ||
                      errors.acquisitionMode ||
                      errors.accession) && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                        <p className="text-sm text-red-700">
                          {errors.acquisitionMode ||
                            errors.accession ||
                            errors.itemTypeId ||
                            errors.locationId ||
                            errors.conditionCode ||
                            errors.statusCode}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Basic Information */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                          <BookOpen className="w-5 h-5 text-primary" />
                          Basic Information
                        </h2>

                        <div className="space-y-4">
                          <div>
                            <RequiredLabel
                              htmlFor="title"
                              required={isFieldRequiredOnPublish("MANUAL", "title")}
                              error={errors.title}
                            >
                              Title
                            </RequiredLabel>
                            <Input
                              id="title"
                              value={formData.biblio.title}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, title: e.target.value },
                                }))
                              }
                              placeholder="Enter the book title"
                              className={cn("mt-1.5", errors.title && "border-red-500")}
                            />
                            {errors.title && (
                              <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4" />
                                {errors.title}
                              </p>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="subtitle">Subtitle (optional)</Label>
                            <Input
                              id="subtitle"
                              value={formData.biblio.subtitle || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, subtitle: e.target.value },
                                }))
                              }
                              placeholder="e.g., A Modern Approach"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Will be displayed as "Title: Subtitle" on catalog cards
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="isbn" className="flex items-center gap-1.5">
                              <Barcode className="w-4 h-4 text-gray-400" />
                              ISBN <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn"
                              value={formData.biblio.isbn}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, isbn: e.target.value },
                                }))
                              }
                              placeholder="ISBN-10 or ISBN-13"
                              className={cn("mt-1.5", errors.isbn && "border-red-500")}
                            />
                            {errors.isbn && (
                              <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4" />
                                {errors.isbn}
                              </p>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="doi">DOI (optional)</Label>
                            <Input
                              id="doi"
                              value={formData.biblio.doi}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, doi: e.target.value },
                                }))
                              }
                              placeholder="Digital Object Identifier"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                              id="description"
                              value={formData.biblio.description}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, description: e.target.value },
                                }))
                              }
                              placeholder="Enter a brief description or summary"
                              rows={3}
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label htmlFor="coverUrl" className="flex items-center gap-1.5">
                              <BookMarked className="w-4 h-4 text-gray-400" />
                              Cover Image URL
                            </Label>
                            <Input
                              id="coverUrl"
                              value={formData.biblio.coverUrl}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  biblio: { ...prev.biblio, coverUrl: e.target.value },
                                }))
                              }
                              placeholder="https://example.com/cover.jpg (optional)"
                              className="mt-1.5"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>

                    {/* Authors */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                          <User className="w-5 h-5 text-primary" />
                          Authors <span className="text-red-500">*</span>
                        </h2>

                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter author name and press Enter"
                            value={authorInput}
                            onChange={(e) => setAuthorInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addAuthor();
                              }
                            }}
                            className={cn("flex-1", errors.authors && "border-red-500")}
                          />
                          <Button type="button" variant="outline" onClick={addAuthor}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>

                        {errors.authors && (
                          <p className="flex items-center gap-1 mt-2 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            {errors.authors}
                          </p>
                        )}

                        {formData.biblio.authors && formData.biblio.authors.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.biblio.authors.map((author, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                              >
                                {author}
                                <button
                                  type="button"
                                  onClick={() => removeAuthor(index)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>

                    {/* Editors */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                          <User className="w-5 h-5 text-primary" />
                          Editors (optional)
                        </h2>
                        <p className="text-sm text-gray-500 mb-3">
                          For textbooks and edited volumes. First editor = Editor-in-Chief.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter editor name and press Enter"
                            value={editorInput}
                            onChange={(e) => setEditorInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addEditor();
                              }
                            }}
                            className="flex-1"
                          />
                          <Button type="button" variant="outline" onClick={addEditor}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {formData.biblio.editors && formData.biblio.editors.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.biblio.editors.map((editor, index) => (
                              <span
                                key={index}
                                className={cn(
                                  "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm",
                                  index === 0
                                    ? "bg-primary text-white"
                                    : "bg-primary/10 text-primary"
                                )}
                              >
                                {index === 0 && <span className="text-xs">(Editor-in-Chief)</span>}
                                {editor}
                                <button
                                  type="button"
                                  onClick={() => removeEditor(index)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>

                    {/* Publication Details */}
                    <CatalogCollapsibleFormSection
                      title="Publication Details"
                      icon={<Building className="h-5 w-5 text-primary" />}
                      collapsible={!isEditMode}
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div>
                          <Label htmlFor="publisher">
                            Publisher <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="publisher"
                            value={formData.biblio.publisher}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, publisher: e.target.value },
                              }))
                            }
                            placeholder="Publisher name"
                            className={cn("mt-1.5", errors.publisher && "border-red-500")}
                          />
                          {errors.publisher && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {errors.publisher}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="placeOfPublication">
                            Place of Publication <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="placeOfPublication"
                            value={formData.biblio.placeOfPublication}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, placeOfPublication: e.target.value },
                              }))
                            }
                            placeholder="e.g., Lagos, New York"
                            className={cn("mt-1.5", errors.placeOfPublication && "border-red-500")}
                          />
                          {errors.placeOfPublication && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {errors.placeOfPublication}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="publishedDate" className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Published Date <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="publishedDate"
                            value={formData.biblio.publishedDate}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, publishedDate: e.target.value },
                              }))
                            }
                            placeholder="e.g., 2024"
                            className={cn("mt-1.5", errors.publishedDate && "border-red-500")}
                          />
                          {errors.publishedDate && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {errors.publishedDate}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mt-4">
                        <div>
                          <Label htmlFor="edition">Edition (optional)</Label>
                          <Input
                            id="edition"
                            value={formData.biblio.edition || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, edition: e.target.value },
                              }))
                            }
                            placeholder="e.g., 2nd ed, 3rd ed, revised"
                            className="mt-1.5"
                          />
                          <p className="mt-1 text-xs text-gray-500">Leave empty for 1st edition</p>
                        </div>
                        <div>
                          <Label htmlFor="seriesInformation">Series Information</Label>
                          <Input
                            id="seriesInformation"
                            value={formData.biblio.seriesInformation}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, seriesInformation: e.target.value },
                              }))
                            }
                            placeholder="e.g., Oxford Studies, Vol. 5"
                            className="mt-1.5"
                          />
                          <p className="mt-1 text-xs text-gray-500">Include volume information</p>
                        </div>
                        <div>
                          <Label htmlFor="pages">Pages</Label>
                          <Input
                            id="pages"
                            type="number"
                            value={formData.biblio.pages || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: {
                                  ...prev.biblio,
                                  pages: e.target.value ? parseInt(e.target.value) : undefined,
                                },
                              }))
                            }
                            placeholder="Number of pages"
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
                        <div>
                          <Label htmlFor="illustrations">Illustrations (optional)</Label>
                          <Input
                            id="illustrations"
                            value={formData.biblio.illustrations}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, illustrations: e.target.value },
                              }))
                            }
                            placeholder="e.g., ILL, col. ill., maps"
                            className="mt-1.5"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Physical description - illustrations
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="size">Size (optional)</Label>
                          <Input
                            id="size"
                            value={formData.biblio.size}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: { ...prev.biblio, size: e.target.value },
                              }))
                            }
                            placeholder="e.g., iv, 24 cm"
                            className="mt-1.5"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Physical description - size/dimensions
                          </p>
                        </div>
                      </div>
                    </CatalogCollapsibleFormSection>

                    {/* Classification */}
                    <CatalogCollapsibleFormSection
                      title="Classification & Cataloging"
                      icon={<Hash className="h-5 w-5 text-primary" />}
                      collapsible={!isEditMode}
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label htmlFor="callNumber">
                            Call Number <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="callNumber"
                            value={formData.biblio.callNumber}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: {
                                  ...prev.biblio,
                                  callNumber: e.target.value.toUpperCase(),
                                },
                              }))
                            }
                            placeholder="e.g., DT515.83.P2 or DT515.4.F2 2001"
                            className={cn(
                              "mt-1.5 font-mono",
                              errors.callNumber && "border-red-500"
                            )}
                            style={{ textTransform: "uppercase" }}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Automatically converted to UPPERCASE
                          </p>
                          {errors.callNumber && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {errors.callNumber}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="bindingType">
                            Binding Type / Publication Format{" "}
                            <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formData.biblio.bindingType || ""}
                            onValueChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                biblio: {
                                  ...prev.biblio,
                                  bindingType: value || undefined,
                                },
                              }))
                            }
                          >
                            <SelectTrigger
                              className={cn("mt-1.5", errors.bindingType && "border-red-500")}
                            >
                              <SelectValue placeholder="Select binding" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hbk">Hardback (HBK)</SelectItem>
                              <SelectItem value="pbk">Paperback (PBK)</SelectItem>
                              <SelectItem value="audiobook">Audiobook</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.bindingType && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {errors.bindingType}
                            </p>
                          )}
                        </div>
                      </div>
                    </CatalogCollapsibleFormSection>

                    {/* Subjects */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                          <Tag className="w-5 h-5 text-primary" />
                          <RequiredLabel required={isFieldRequiredOnPublish("MANUAL", "subjects")}>
                            Subjects
                          </RequiredLabel>
                        </div>

                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter subject and press Enter"
                            value={subjectInput}
                            onChange={(e) => setSubjectInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addSubject();
                              }
                            }}
                            className={cn("flex-1", errors.subjects && "border-red-500")}
                          />
                          <Button type="button" variant="outline" onClick={addSubject}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>

                        {errors.subjects && (
                          <p className="flex items-center gap-1 mt-2 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            {errors.subjects}
                          </p>
                        )}

                        {formData.biblio.subjects && formData.biblio.subjects.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.biblio.subjects.map((subject, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                              >
                                {subject}
                                <button
                                  type="button"
                                  onClick={() => removeSubject(index)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>

                    {/* Item Details - Only show when creating new book */}
                    {!isEditMode && (
                      <Card className="overflow-hidden bg-white shadow-sm">
                        <div className="p-6">
                          <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                            <Package className="w-5 h-5 text-primary" />
                            Physical Copies
                          </h2>

                          {/* Accession Mode Info */}
                          {catalogSettings.accessionNumberMode === "MANUAL" && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                              <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-amber-900">
                                  Manual Accession Mode
                                </p>
                                <p className="text-xs text-amber-700">
                                  Your library is configured to require manual entry of accession
                                  numbers. Each copy must have an accession number assigned.
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
                            {/* Number of Copies */}
                            <div>
                              <Label>Number of Copies</Label>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={decrementCopies}
                                  disabled={formData.copies <= 1}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={formData.copies}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      copies: Math.max(
                                        1,
                                        Math.min(100, parseInt(e.target.value) || 1)
                                      ),
                                    }))
                                  }
                                  className="w-20 text-center"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={incrementCopies}
                                  disabled={formData.copies >= 100}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Item Type */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <FileText className="w-4 h-4 text-gray-400" />
                                Item Type <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={formData.itemDefaults.itemTypeId || ""}
                                onValueChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    itemDefaults: {
                                      ...prev.itemDefaults,
                                      itemTypeId: value || undefined,
                                    },
                                  }))
                                }
                              >
                                <SelectTrigger
                                  className={cn("mt-1.5", errors.itemTypeId && "border-red-500")}
                                >
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {itemTypes.map((type) => (
                                    <SelectItem key={type.id} value={type.id}>
                                      {type.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors.itemTypeId && (
                                <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                  <AlertCircle className="w-4 h-4" />
                                  {errors.itemTypeId}
                                </p>
                              )}
                            </div>

                            {/* Location */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                Default Location <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={formData.itemDefaults.locationId || ""}
                                onValueChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    itemDefaults: {
                                      ...prev.itemDefaults,
                                      locationId: value || undefined,
                                    },
                                  }))
                                }
                              >
                                <SelectTrigger
                                  className={cn("mt-1.5", errors.locationId && "border-red-500")}
                                >
                                  <SelectValue placeholder="Select location" />
                                </SelectTrigger>
                                <SelectContent>
                                  {locations.map((location) => (
                                    <SelectItem key={location.id} value={location.id}>
                                      {location.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors.locationId && (
                                <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                  <AlertCircle className="w-4 h-4" />
                                  {errors.locationId}
                                </p>
                              )}
                            </div>

                            {/* Condition */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <ClipboardCheck className="w-4 h-4 text-gray-400" />
                                Default Condition <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={formData.itemDefaults.conditionCode || ""}
                                onValueChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    itemDefaults: {
                                      ...prev.itemDefaults,
                                      conditionCode: value || undefined,
                                    },
                                  }))
                                }
                              >
                                <SelectTrigger
                                  className={cn("mt-1.5", errors.conditionCode && "border-red-500")}
                                >
                                  <SelectValue placeholder="Select condition" />
                                </SelectTrigger>
                                <SelectContent>
                                  {conditions.map((cond) => (
                                    <SelectItem key={cond.code} value={cond.code}>
                                      {cond.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors.conditionCode && (
                                <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                  <AlertCircle className="w-4 h-4" />
                                  {errors.conditionCode}
                                </p>
                              )}
                            </div>

                            {/* Status */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4 text-gray-400" />
                                Default Status <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={formData.itemDefaults.statusCode || ""}
                                onValueChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    itemDefaults: {
                                      ...prev.itemDefaults,
                                      statusCode: value || "AVAILABLE",
                                    },
                                  }))
                                }
                              >
                                <SelectTrigger
                                  className={cn("mt-1.5", errors.statusCode && "border-red-500")}
                                >
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {itemStatuses.map((status) => (
                                    <SelectItem key={status.code} value={status.code}>
                                      {status.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors.statusCode && (
                                <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                  <AlertCircle className="w-4 h-4" />
                                  {errors.statusCode}
                                </p>
                              )}
                            </div>

                            {/* Collection */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <Layers className="w-4 h-4 text-gray-400" />
                                Default Collection
                              </Label>
                              <Select
                                value={formData.itemDefaults.collectionId || "none"}
                                onValueChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    itemDefaults: {
                                      ...prev.itemDefaults,
                                      collectionId: value === "none" ? undefined : value,
                                    },
                                  }))
                                }
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue placeholder="Select collection" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {collections.map((collection) => (
                                    <SelectItem key={collection.id} value={collection.id}>
                                      {collection.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Branch */}
                            {branches.length > 0 && (
                              <div>
                                <Label className="flex items-center gap-1.5">
                                  <Building className="w-4 h-4 text-gray-400" />
                                  Default Branch <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={formData.itemDefaults.branchId || ""}
                                  onValueChange={(value) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      itemDefaults: {
                                        ...prev.itemDefaults,
                                        branchId: value || undefined,
                                      },
                                    }))
                                  }
                                >
                                  <SelectTrigger
                                    className={cn("mt-1.5", errors.branchId && "border-red-500")}
                                  >
                                    <SelectValue placeholder="Select branch" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {branches.map((branch) => (
                                      <SelectItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {errors.branchId && (
                                  <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                    <AlertCircle className="w-4 h-4" />
                                    {errors.branchId}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Per-Copy Details Editor */}
                          <div className="pt-4 border-t border-gray-200">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">
                              Copy Details
                              <span className="ml-2 text-red-500 text-xs font-normal">
                                * Acquisition mode required for each copy
                              </span>
                              {catalogSettings.accessionNumberMode === "MANUAL" && (
                                <span className="ml-2 text-red-500 text-xs font-normal">
                                  * Accession numbers required
                                </span>
                              )}
                            </h3>
                            <CopyDetailsEditor
                              copies={formData.copies}
                              copyData={copyData}
                              onCopyDataChange={setCopyData}
                              conditions={conditions}
                              itemStatuses={itemStatuses}
                              locations={locations}
                              branches={branches}
                              isManualMode={catalogSettings.accessionNumberMode === "MANUAL"}
                              defaultCondition={formData.itemDefaults.conditionCode}
                              defaultStatus={formData.itemDefaults.statusCode || "AVAILABLE"}
                              defaultLocationId={formData.itemDefaults.locationId}
                              defaultBranchId={formData.itemDefaults.branchId}
                            />
                          </div>

                          {/* Validation Errors */}
                          {(errors.accession || errors.acquisitionMode) && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                              <p className="text-sm text-red-700">
                                {errors.accession || errors.acquisitionMode}
                              </p>
                            </div>
                          )}
                        </div>
                      </Card>
                    )}
                  </>
                )}

                {/* Existing Copies — edit mode only: view and edit current copies */}
                {isEditMode && existingItems.length > 0 && (
                  <Card className="overflow-hidden bg-white shadow-sm">
                    <div className="p-6">
                      <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                        <Package className="w-5 h-5 text-primary" />
                        Existing Copies ({formatNumberWithCommas(existingItems.length)})
                      </h2>
                      <p className="text-sm text-gray-500 mb-4">
                        Edit branch, location, and other details for each copy. Changes are saved
                        immediately when you click Save.
                      </p>
                      <div className="space-y-4">
                        {existingItems.map((item: any) => (
                          <ExistingCopyEditor
                            key={item.id}
                            item={item}
                            branches={branches}
                            itemTypes={itemTypes}
                            locations={locations}
                            collections={collections}
                            conditions={conditions}
                            itemStatuses={itemStatuses}
                            savingItemId={savingItemId}
                            canEdit={canEditCopy}
                            canDelete={canDeleteCopy}
                            onSave={async (updates) => {
                              setSavingItemId(item.id);
                              try {
                                const updated = await catalogApi.updateItem(item.id, updates);
                                setExistingItems((prev) =>
                                  prev.map((i) => (i.id === item.id ? { ...i, ...updated } : i))
                                );
                                toast.success(`Copy #${item.copyNumber} updated.`);
                              } catch (err: any) {
                                toast.error(
                                  err.response?.data?.message || "Failed to update copy."
                                );
                              } finally {
                                setSavingItemId(null);
                              }
                            }}
                            onItemUpdated={(updated) => {
                              setExistingItems((prev) =>
                                prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i))
                              );
                            }}
                            onItemRemoved={(itemId) => {
                              setExistingItems((prev) => prev.filter((i) => i.id !== itemId));
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Add Copies — edit mode only */}
                {isEditMode && (
                  <Card className="overflow-hidden bg-white shadow-sm">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                          <Plus className="w-5 h-5 text-primary" />
                          Add More Copies
                        </h2>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddCopies(!showAddCopies)}
                        >
                          {showAddCopies ? "Cancel" : "Add Copies"}
                        </Button>
                      </div>

                      {!showAddCopies && (
                        <p className="text-sm text-gray-500">
                          {existingItems.length > 0
                            ? 'Click "Add Copies" to add additional physical copies to this bibliographic record.'
                            : 'Click "Add Copies" to add physical copies to this bibliographic record.'}
                        </p>
                      )}

                      {showAddCopies && (
                        <div className="space-y-4">
                          {catalogSettings.accessionNumberMode === "MANUAL" && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                              <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                              <p className="text-sm text-amber-700">
                                Manual accession mode is active. You must assign accession numbers
                                to each copy.
                              </p>
                            </div>
                          )}

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {/* Number of Copies */}
                            <div>
                              <Label>Number of Copies to Add</Label>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setNewCopiesCount((c) => Math.max(1, c - 1));
                                    setNewCopyData((d) => d.slice(0, -1));
                                  }}
                                  disabled={newCopiesCount <= 1}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <Input
                                  type="number"
                                  min="1"
                                  max="50"
                                  value={newCopiesCount}
                                  onChange={(e) =>
                                    setNewCopiesCount(
                                      Math.max(1, Math.min(50, parseInt(e.target.value) || 1))
                                    )
                                  }
                                  className="w-20 text-center"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setNewCopiesCount((c) => Math.min(50, c + 1));
                                    setNewCopyData((d) => [...d, {}]);
                                  }}
                                  disabled={newCopiesCount >= 50}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Item Type */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <FileText className="w-4 h-4 text-gray-400" />
                                Item Type
                              </Label>
                              <Select
                                value={newCopyItemDefaults.itemTypeId || "none"}
                                onValueChange={(v) =>
                                  setNewCopyItemDefaults((p) => ({
                                    ...p,
                                    itemTypeId: v === "none" ? "" : v,
                                  }))
                                }
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {itemTypes.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                      {t.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Location */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                Location
                              </Label>
                              <Select
                                value={newCopyItemDefaults.locationId || "none"}
                                onValueChange={(v) =>
                                  setNewCopyItemDefaults((p) => ({
                                    ...p,
                                    locationId: v === "none" ? "" : v,
                                  }))
                                }
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue placeholder="Select location" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {locations.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>
                                      {l.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Condition */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <ClipboardCheck className="w-4 h-4 text-gray-400" />
                                Condition
                              </Label>
                              <Select
                                value={newCopyItemDefaults.conditionCode || "none"}
                                onValueChange={(v) =>
                                  setNewCopyItemDefaults((p) => ({
                                    ...p,
                                    conditionCode: v === "none" ? "" : v,
                                  }))
                                }
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue placeholder="Select condition" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {conditions.map((c) => (
                                    <SelectItem key={c.code} value={c.code}>
                                      {c.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Status */}
                            <div>
                              <Label className="flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4 text-gray-400" />
                                Status
                              </Label>
                              <Select
                                value={newCopyItemDefaults.statusCode || "AVAILABLE"}
                                onValueChange={(v) =>
                                  setNewCopyItemDefaults((p) => ({ ...p, statusCode: v }))
                                }
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {itemStatuses.map((s) => (
                                    <SelectItem key={s.code} value={s.code}>
                                      {s.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Branch */}
                            {branches.length > 0 && (
                              <div>
                                <Label className="flex items-center gap-1.5">
                                  <Building className="w-4 h-4 text-gray-400" />
                                  Branch <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={newCopyItemDefaults.branchId || ""}
                                  onValueChange={(v) =>
                                    setNewCopyItemDefaults((p) => ({
                                      ...p,
                                      branchId: v,
                                    }))
                                  }
                                >
                                  <SelectTrigger
                                    className={cn("mt-1.5", errors.branchId && "border-red-500")}
                                  >
                                    <SelectValue placeholder="Select branch" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {branches.map((b) => (
                                      <SelectItem key={b.id} value={b.id}>
                                        {b.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {errors.branchId && (
                                  <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                                    <AlertCircle className="w-4 h-4" />
                                    {errors.branchId}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="pt-4 border-t border-gray-200">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Copy Details</h3>
                            <CopyDetailsEditor
                              copies={newCopiesCount}
                              copyData={newCopyData}
                              onCopyDataChange={setNewCopyData}
                              conditions={conditions}
                              itemStatuses={itemStatuses}
                              locations={locations}
                              branches={branches}
                              isManualMode={catalogSettings.accessionNumberMode === "MANUAL"}
                              defaultCondition={newCopyItemDefaults.conditionCode}
                              defaultStatus={newCopyItemDefaults.statusCode || "AVAILABLE"}
                              defaultLocationId={newCopyItemDefaults.locationId}
                              defaultBranchId={newCopyItemDefaults.branchId}
                            />
                          </div>

                          <div className="flex justify-end pt-2">
                            <Button
                              type="button"
                              disabled={addingCopies}
                              onClick={async () => {
                                try {
                                  setAddingCopies(true);
                                  const result = await catalogApi.addCopiesToBiblio(id!, {
                                    copies: newCopiesCount,
                                    itemDefaults: {
                                      itemTypeId: newCopyItemDefaults.itemTypeId || undefined,
                                      locationId: newCopyItemDefaults.locationId || undefined,
                                      collectionId: newCopyItemDefaults.collectionId || undefined,
                                      branchId: newCopyItemDefaults.branchId || undefined,
                                      conditionCode: newCopyItemDefaults.conditionCode || undefined,
                                      statusCode: newCopyItemDefaults.statusCode || "AVAILABLE",
                                      acquisitionMode:
                                        newCopyItemDefaults.acquisitionMode || undefined,
                                    },
                                    itemCopies: newCopyData.map((c) => ({
                                      accessionNumber: c.accessionNumber,
                                      conditionCode: c.conditionCode,
                                      statusCode: c.statusCode,
                                      acquisitionMode: c.acquisitionMode,
                                      locationId: c.locationId,
                                      branchId: c.branchId,
                                    })),
                                  });
                                  toast.success(
                                    `${result.items.length} ${result.items.length === 1 ? "copy" : "copies"} added successfully!`
                                  );
                                  // Refresh existing items to include the new copies
                                  const refreshed = await catalogApi.getBiblio(id!);
                                  setExistingItems(refreshed.Items || []);
                                  setFormData((prev) => ({
                                    ...prev,
                                    copies: refreshed.Items?.length || prev.copies,
                                  }));
                                  setShowAddCopies(false);
                                  setNewCopiesCount(1);
                                  setNewCopyData([{}]);
                                  setNewCopyItemDefaults({
                                    itemTypeId: "",
                                    locationId: "",
                                    collectionId: "",
                                    branchId: "",
                                    conditionCode: "",
                                    statusCode: "AVAILABLE",
                                    acquisitionMode: "",
                                  });
                                } catch (err: any) {
                                  toast.error(
                                    err.response?.data?.message || "Failed to add copies"
                                  );
                                } finally {
                                  setAddingCopies(false);
                                }
                              }}
                            >
                              {addingCopies ? (
                                <>
                                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent mr-2" />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add {formatNumberWithCommas(newCopiesCount)}{" "}
                                  {newCopiesCount === 1 ? "Copy" : "Copies"}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className={cn("space-y-4", !isEditMode && "hidden lg:block")}>
                {isAddCopyCreateFlow ? (
                  <Card className="sticky top-4 overflow-hidden bg-white shadow-sm">
                    <div className="space-y-3 p-6">
                      <h3 className="font-semibold text-gray-900">Add copy</h3>
                      {addCopyActionButton}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="w-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <Card className="sticky top-4 overflow-hidden bg-white shadow-sm">
                    <div className="p-6">
                      <h3 className="flex items-center gap-2 mb-4 font-semibold text-gray-900">
                        <BookOpen className="w-4 h-4 text-primary" />
                        Summary
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Title</span>
                          <span className="font-medium text-gray-900 text-right max-w-[180px] truncate">
                            {formData.biblio.title || "—"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Authors</span>
                          <span className="font-medium text-gray-900">
                            {formData.biblio.authors?.length || 0}
                          </span>
                        </div>
                        {!isEditMode && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Copies</span>
                            <span className="font-medium text-gray-900">
                              {formatNumberWithCommas(formData.copies)}
                            </span>
                          </div>
                        )}
                        {formData.biblio.isbn && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">ISBN</span>
                            <span className="font-mono text-xs text-gray-900">
                              {formData.biblio.isbn}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-6 border-t border-gray-100 bg-gray-50">
                      <CatalogWorkflowButtons
                        catalogSettings={catalogSettings}
                        loading={loading}
                        isEditMode={isEditMode}
                        publicationStatus={publicationStatus}
                        isReadOnly={isReadOnly}
                        createLabel="Catalog Book"
                        onAction={handleWorkflowAction}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="w-full mt-2"
                      >
                        Cancel
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Tips */}
                {!isAddCopyCreateFlow && (
                  <Card className="p-4 bg-white shadow-sm">
                    <h4 className="mb-2 font-medium text-gray-900">Tips</h4>
                    <ul className="space-y-1 text-xs text-gray-500">
                      <li>• Title and at least one author are required</li>
                      {isEditMode ? (
                        <>
                          <li>• Changes will update the bibliographic record</li>
                          <li>• Item copies are managed separately</li>
                        </>
                      ) : (
                        <>
                          <li>• ISBN helps with deduplication</li>
                          <li>• Use ISBN Auto-Catalog for faster entry</li>
                        </>
                      )}
                    </ul>
                  </Card>
                )}
              </div>
            </div>
          </fieldset>
        </form>
      </div>

      {!isEditMode && shouldShowCreateForm && (
        <CatalogMobileActionBar>
          <CatalogWorkflowButtons
            catalogSettings={catalogSettings}
            loading={loading}
            isEditMode={false}
            publicationStatus={publicationStatus}
            isReadOnly={isReadOnly}
            createLabel="Catalog Book"
            onAction={handleWorkflowAction}
            layout="inline"
          />
          <Button type="button" variant="outline" onClick={handleCancel} className="w-full">
            Cancel
          </Button>
        </CatalogMobileActionBar>
      )}

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscard}
        variant="warning"
      />
    </div>
  );
}
